/* eslint-disable ember/no-runloop */
import { fn } from '@ember/helper';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, get, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { getOwner } from '@ember/owner';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import * as Highcharts from 'highcharts';
import GeneralChartsLegendsLegendItem from 'lumen/components/general/charts/legends/legend-item';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelAndMarketRevenueInsightTooltipsTooltipStayDate from 'lumen/components/hotel-and-market/revenue-insight/tooltips/tooltip-stay-date';
import type { RevenueInsightSegmentationValue } from 'lumen/models';
import { isSegmentationDataKey } from 'lumen/models/revenue-insight/segmentation-data';
import { xValueTomorrow } from 'lumen/utils/charts/chart-utils';
import { addNoDataZone, addZone, NO_DATA_ZONE } from 'lumen/utils/charts/zones';
import { compsetDifference } from 'lumen/utils/compset-difference';
import {
  CHART_INTERVALS,
  HALF_DAY,
  HIGHCHARTS_MISSING_MARKER,
} from 'lumen/utils/constants/general-constants';
import { DETAIL_TABS } from 'lumen/utils/constants/rate-constants';
import {
  CHART_CANCELLATIONS_PATTERN,
  EMPTY_SEGMENTATION_VALUE,
  KPI_CATEGORIES,
  type KpiCategory,
  KPIS,
  MAX_DRAGGABLE_DAYS,
  NORMALIZED_PERIOD,
  SUMMABLE_SHORT_KEYS,
  type ValidKpi,
} from 'lumen/utils/constants/ri-constant-values';
import CalculatedValues from 'lumen/utils/datamodels/revenue-insight/calculated-values';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';
import { OutOfOrderRooms } from 'lumen/utils/models/occupancy';
import findMedianRate from 'lumen/utils/numeric/median';
import safeDivide from 'lumen/utils/numeric/safe-divide';
import { timeout } from 'lumen/utils/waiters';

type AggregatedChartDataBase = {
  [key in Services['ri-selections']['valuesToCalculate'][number]]?: typeof EMPTY_SEGMENTATION_VALUE;
};

interface AggregatedChartData extends AggregatedChartDataBase {
  calculatedValues: CalculatedValues;
  id: string;
  isIncompletePeriod: boolean;
  segmentationId: string;
  segmentationSortValue: string;
  segmentationValue: string;
  segmentationValueId: string;
  totalCapacity: number;
  unfilteredId: string;
}

interface RatesPoint {
  marker?: unknown;
  segmentationValueObj?: RevenueInsightSegmentationValue | AggregatedChartData;
  x: number;
  y: number | null | undefined;
}

interface DataPoint {
  segmentationValueObj?: RevenueInsightSegmentationValue | AggregatedChartData;
  stackedY?: number;
  x: number;
  y: number | null;
}

interface HotelAndMarketRevenueInsightChartsChartStayDateSignature {
  Args: {
    aggregationPeriod: 'day' | 'week' | 'month';
    chartSelectionChanged: (
      id: string,
      range: { end: Dayjs; start: Dayjs },
    ) => void;
    data: RevenueInsightSegmentationValue[];
    filterLimits: { [RI_BREAKDOWNS.IMPACT_DATE]: number };
    filtersActive: boolean;
    fromDate: string;
    isLoading: boolean;
    isRatesEnabled: boolean;
    kpi: ValidKpi;
    kpiCategory: KpiCategory;
    onUpdateChartReference: (chart: Highcharts.Chart) => void;
    openDayDetail: (type: string, date: string) => void;
    parsedStayDateFilter: { endDate: string; startDate: string } | null;
    toDate: string;
    visibleKpis: ValidKpi[];
  };
}

export default class HotelAndMarketRevenueInsightChartsChartStayDate extends Component<HotelAndMarketRevenueInsightChartsChartStayDateSignature> {
  @service declare access: Services['access'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Any segmentation filters active?
  filtersActive = false;
  isBudgetVisible = true;
  isCancellationsLastYearVisible = true;
  isCancellationsThisYearVisible = true;
  isCancellationsVisible = false;
  isCapacityVisible = true;
  isChangeVisible = true;
  isForecastVisible = true;
  isPickupLastYearVisible = true;
  isRatesVisible = true;
  isThisYearVisible = true;
  parsedStayDateFilter = null;

  tooltipData: RevenueInsightSegmentationValue | AggregatedChartData | null =
    null;

  updateFilterTask = task(
    { drop: true },
    async (zoomStartDateRaw, zoomEndDateRaw): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      let zoomStartDate = zoomStartDateRaw;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      let zoomEndDate = zoomEndDateRaw;
      // Only allow drag and drop when it's single property
      // Or for multi property and the aggregation is by day
      if (
        this.riSelections.isSingleProperty ||
        (this.riSelections.isMultiProperty &&
          this.args.aggregationPeriod === 'day')
      ) {
        // For multiproperty we only allow MAX_DRAGGABLE_DAYS days
        // Diff + 1 because it is not inclusive
        if (
          this.riSelections.isMultiProperty &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          zoomEndDate.diff(zoomStartDate, 'day') + 1 > MAX_DRAGGABLE_DAYS
        ) {
          // Add -1 becasue including the first day it's MAX_DRAGGABLE_DAYS
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          zoomEndDate = zoomStartDate.add(MAX_DRAGGABLE_DAYS - 1, 'day');
        }

        if (
          this.args.aggregationPeriod === 'week' ||
          this.args.aggregationPeriod === 'month'
        ) {
          const aggregationPeriodNormalized =
            NORMALIZED_PERIOD[this.args.aggregationPeriod];
          // If the drag starts at the first possible date, do not add one period (otherwise the first period is not selectable)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          if (!zoomStartDate.isSame(dayjs.utc(this.xAxisFloor), 'day')) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            zoomStartDate
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              .add(1, this.args.aggregationPeriod)
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              .startOf(aggregationPeriodNormalized);
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          zoomEndDate.endOf(aggregationPeriodNormalized);
        }
        // Make sure the selection period is within the bounds
        const minStartDate = dayjs.utc(
          this.args.parsedStayDateFilter
            ? this.args.parsedStayDateFilter?.startDate
            : this.args.fromDate,
        );
        const maxEndDate = dayjs.utc(
          this.args.parsedStayDateFilter
            ? this.args.parsedStayDateFilter?.endDate
            : this.args.toDate,
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        zoomStartDate =
          zoomStartDate > minStartDate ? zoomStartDate : minStartDate;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        zoomEndDate = zoomEndDate < maxEndDate ? zoomEndDate : maxEndDate;

        this.usageAnalytics.track('Stay Date Range Changed', {
          eventTrigger: 'stay-date-chart',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          amountOfDays: zoomEndDate.diff(zoomStartDate, 'days'),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          startDaysOffsetFromToday: zoomStartDate.diff(
            this.hotelAndMarketService.getLocalTime(),
            'days',
          ),
        });

        const range = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          start: zoomStartDate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          end: zoomEndDate,
        };

        this.args.chartSelectionChanged(RI_BREAKDOWNS.IMPACT_DATE, range);
      }

      // The set extremes could trigger multiple times, only adjust the date filter once
      await timeout();
    },
  );

  /**
   * Creates series data with the capacity info
   * @return { object }   : series info with x, y and the object (so we can use it in the tooltip)
   */
  @computed('args.kpi', 'filteredAndSortedData.@each.value')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get capacityData() {
    const showPercentage = this.args.kpi === KPIS.OCCUPANCY;

    return this.filteredAndSortedData.map((item) => {
      const xValue = this.hotelAndMarketService.toLocalUnixTime(
        this.hotelAndMarketService.toLocalTime(item.segmentationValue).unix(),
      );

      if (
        item.value?.capacity === undefined ||
        item.value?.capacity === null ||
        !('nrOfRoomsOutOfOrder' in item.value) ||
        item.value?.nrOfRoomsOutOfOrder === undefined ||
        item.value?.nrOfRoomsOutOfOrder === null
      ) {
        return {
          y: null,
          x: xValue,
          segmentationValueObj: item,
        };
      }

      const inventoryExclOOO =
        item.value.capacity - item.value.nrOfRoomsOutOfOrder;
      const inventoryExclOOOPercentage = item.value.capacity
        ? (inventoryExclOOO / item.value?.capacity) * 100
        : 0;

      return {
        y: showPercentage ? inventoryExclOOOPercentage : inventoryExclOOO,
        x: xValue,
        segmentationValueObj: item,
      };
    });
  }

  @computed(
    'filteredAndSortedData.[]',
    'args.{kpi,isRatesEnabled}',
    'capacityData.[]',
    'ratesData.[]',
    'isCapacityVisible',
    'isCancellationsVisible',
    'isCancellationsThisYearVisible',
    'isCancellationsLastYearVisible',
    'isCapacityEnabled',
    'isThisYearVisible',
    'isChangeVisible',
    'isRatesDataActive',
    'isChangeEnabled',
    'isBudgetEnabled',
    'isBudgetVisible',
    'isForecastEnabled',
    'isForecastVisible',
    'isPickupLastYearVisible',
    'isPickupLastYearEnabled',
    'ratesDisabledForSelection',
    'riSelections.{bobKey,referenceKey,hasCancellations}',
    'isCancellationsOnlyView',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const filteredChartData = this.filteredAndSortedData;
    const series = [];

    const resBOB: DataPoint[] = [];
    const resChange: DataPoint[] = [];
    const resBudget: DataPoint[] = [];
    const resForecast: DataPoint[] = [];
    const resPickupLY: DataPoint[] = [];
    const resCancellations: DataPoint[] = [];
    const resCancellationsThisYear: DataPoint[] = [];
    const resCancellationsLastYear: DataPoint[] = [];

    const { bobKey, referenceKey, hasCancellations } = this.riSelections;

    filteredChartData.forEach((item) => {
      let resBOBYValue = item.calculatedValues.BOB[this.args.kpi];

      // This check is necessary to prevent errors with stacked bars. If you allow the bottom stacked serie to have `null` values,
      // then the top stacked serie doesn't know how to translate the points upwards, and it hard-fails in Highcharts. Hard to track down.
      // By setting all `null` points to `0`, we prevent this crash, and the desired result is obtained
      if (
        resBOBYValue === null &&
        item.calculatedValues.pickupLastYear[this.args.kpi] !== null &&
        (this.isBudgetEnabled || this.isForecastEnabled)
      ) {
        resBOBYValue = 0;
      }

      if (!this.isCancellationsOnlyView) {
        resBOB.push({
          y: resBOBYValue ?? null,
          x: this.hotelAndMarketService.toLocalUnixTime(
            this.hotelAndMarketService
              .toLocalTime(item.segmentationValue)
              .unix(),
          ),
          segmentationValueObj: item,
        });

        resChange.push({
          y: item.calculatedValues.change[this.args.kpi] ?? null,
          x: this.hotelAndMarketService.toLocalUnixTime(
            this.hotelAndMarketService
              .toLocalTime(item.segmentationValue)
              .unix(),
          ),
          segmentationValueObj: item,
        });

        resBudget.push({
          y: item.calculatedValues.budget[this.args.kpi] ?? null,
          x: this.hotelAndMarketService.toLocalUnixTime(
            this.hotelAndMarketService
              .toLocalTime(item.segmentationValue)
              .unix(),
          ),
          segmentationValueObj: item,
        });

        resForecast.push({
          y: item.calculatedValues.forecast[this.args.kpi] ?? null,
          x: this.hotelAndMarketService.toLocalUnixTime(
            this.hotelAndMarketService
              .toLocalTime(item.segmentationValue)
              .unix(),
          ),
          segmentationValueObj: item,
        });

        resPickupLY.push({
          y: item.calculatedValues.pickupLastYear[this.args.kpi] ?? null,
          x: this.hotelAndMarketService.toLocalUnixTime(
            this.hotelAndMarketService
              .toLocalTime(item.segmentationValue)
              .unix(),
          ),
          // We keep track of the stacked yValue because later on we try to determine the max to prevent the chart going out of bounds
          stackedY:
            (item.calculatedValues.BOB[this.args.kpi] as number) +
            (item.calculatedValues.pickupLastYear[this.args.kpi] as number),
          segmentationValueObj: item,
        });

        if (hasCancellations) {
          if (item.calculatedValues.cancellationBOB !== undefined) {
            resCancellations.push({
              y: item.calculatedValues.cancellationBOB[this.args.kpi] ?? null,
              x: this.hotelAndMarketService.toLocalUnixTime(
                this.hotelAndMarketService
                  .toLocalTime(item.segmentationValue)
                  .unix(),
              ),
              stackedY:
                (item.calculatedValues.cancellationBOB[
                  this.args.kpi
                ] as number) + (resBOBYValue as number),
              segmentationValueObj: item,
            });
          }
        }
      } else {
        if (item.calculatedValues[bobKey] !== undefined) {
          resCancellationsThisYear.push({
            y: item.calculatedValues[bobKey][this.args.kpi] ?? null,
            x: this.hotelAndMarketService.toLocalUnixTime(
              this.hotelAndMarketService
                .toLocalTime(item.segmentationValue)
                .unix(),
            ),
          });
        }

        if (item.calculatedValues[referenceKey] !== undefined) {
          resCancellationsLastYear.push({
            y: item.calculatedValues[referenceKey][this.args.kpi] ?? null,
            x: this.hotelAndMarketService.toLocalUnixTime(
              this.hotelAndMarketService
                .toLocalTime(item.segmentationValue)
                .unix(),
            ),
          });
        }
      }
    });

    if (!this.isCancellationsOnlyView) {
      if (this.isPickupLastYearEnabled) {
        series.push({
          data: resPickupLY,
          visible: this.isPickupLastYearVisible,
          type: 'column',
          name: 'pickupLastYear',
          stack: 'BOB',
          zIndex: 10,
          color: 'var(--prism-color-chart-accent-primary-figure-subdued)',
        });
      }

      if (this.riSelections.hasCancellations) {
        series.push({
          data: resCancellations,
          visible: this.isCancellationsVisible,
          className: 'striped',
          type: 'column',
          name: 'cancellations',
          stack: 'BOB',
          zIndex: 11,
          color: CHART_CANCELLATIONS_PATTERN,
        });
      }
      series.push({
        data: resBOB,
        visible: this.isThisYearVisible,
        type: 'column',
        name: 'BOB',
        color: 'var(--prism-color-chart-accent-primary-figure-default)',
        stack: 'BOB',
      });

      if (this.isChangeEnabled) {
        series.push({
          data: resChange,
          visible: this.isChangeVisible,
          type: 'column',
          name: 'change',
          negativeColor:
            'var(--prism-color-chart-status-figure-critical-normal)',
          color: 'var(--prism-color-chart-status-figure-success-normal)',
        });
      }

      if (this.isBudgetEnabled) {
        series.push({
          data: resBudget,
          visible: this.isBudgetVisible,
          type: 'scatter',
          name: 'budget',
          zIndex: 10,
          color: 'var(--prism-color-target)',
          marker: {
            symbol: 'bar',
            lineWidth: 1,
            // 360 | We use a radius of 12px, and that looked alright for 30 days. 30 * 12 = 360.
            // We also clamp the value to a maximum of 24px (otherwise when filtering, you got very very long target bars)
            radius: Math.min(360 / resBudget.length, 24),
            fillColor:
              'var(--prism-color-chart-accent-secondary-figure-emphasis)',
            lineColor: 'var(--prism-color-border-inverse-emphasis)',
            states: {
              hover: {
                enabled: false,
              },
            },
          },
        });
      }

      if (this.isForecastEnabled) {
        series.push({
          data: resForecast,
          visible: this.isForecastVisible,
          type: 'scatter',
          name: 'forecast',
          zIndex: 10,
          color: 'var(--prism-color-chart-accent-secondary-figure-emphasis)',
          marker: {
            symbol: 'bar',
            lineWidth: 2,
            // 360 | We use a radius of 12px, and that looked alright for 30 days. 30 * 12 = 360.
            // We also clamp the value to a maximum of 24px (otherwise when filtering, you got very very long target bars)
            radius: Math.min(360 / resBudget.length, 24),
            fillColor:
              'var(--prism-color-chart-accent-secondary-figure-emphasis)',
            lineColor:
              'var(--prism-color-chart-accent-secondary-figure-emphasis)',
            states: {
              hover: {
                enabled: false,
              },
            },
          },
        });
      }
    } else {
      series.push({
        data: resCancellationsThisYear,
        visible: this.isCancellationsThisYearVisible,
        type: 'column',
        name: 'cancellationsBOB',
        color: CHART_CANCELLATIONS_PATTERN,
      });
      series.push({
        data: resCancellationsLastYear,
        visible: this.isCancellationsLastYearVisible,
        type: 'column',
        name: 'cancellationsReference',
        color: 'var(--prism-color-chart-accent-secondary-figure-subdued)',
      });
    }

    if (this.args.isRatesEnabled && !this.ratesDisabledForSelection) {
      const { ratesData } = this;

      series.push({
        data: ratesData.data,
        zones: ratesData.zones,
        zoneAxis: 'x',
        visible: this.isRatesDataActive,
        type: 'line',
        name: 'rates',
        zIndex: 10,
        yAxis: 1,
        lineWidth: 2,
        color: 'var(--prism-color-chart-status-figure-warning-normal)',
        marker: {
          enabled: true,
          radius: 4,
        },
      });
    }

    if (this.isCapacityEnabled) {
      series.push({
        data: this.capacityData,
        visible: this.isCapacityVisible,
        type: 'line',
        name: 'capacity',
        zIndex: 10,
        color: 'var(--prism-color-chart-accent-secondary-figure-subdued)',
        marker: {
          // Otherwise you won't see the point
          enabled: this.capacityData.length === 1,
          symbol: 'bar',
          lineWidth: 2,
          fillColor: 'var(--prism-color-chart-accent-secondary-figure-subdued)',
          lineColor: 'var(--prism-color-chart-accent-secondary-figure-subdued)',
          // 360 | We use a radius of 12px, and that looked alright for 30 days. 30 * 12 = 360.
          // We also clamp the value to a maximum of 24px (otherwise when filtering, you got very very long target bars)
          radius: Math.min(360 / this.capacityData.length, 24),
        },
      });
    }

    return series;
  }

  @computed(
    `filterLimits.${RI_BREAKDOWNS.IMPACT_DATE}`,
    'access.hasRates',
    'args.{aggregationPeriod,filterLimits}',
    'chartPlotBands',
    'isCancellationsOnlyView',
    'isCancellationsVisible',
    'isFirst',
    'isLast',
    'point.x',
    'riSelections.{isSingleProperty,isTargetActive}',
    'value',
    'yAxis',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // can only open day detail for the day aggragration in single property
    const canClick =
      this.args.aggregationPeriod === 'day' &&
      this.riSelections.isSingleProperty &&
      this.access.hasRates;
    // Can only drag in day aggragation in multi property, can drag everywhere in single property
    const canDrag =
      (this.args.aggregationPeriod === 'day' ||
        this.riSelections.isSingleProperty) &&
      this.args.filterLimits[RI_BREAKDOWNS.IMPACT_DATE] > 0;

    return {
      chart: {
        type: 'column',
        height: '270px',
        spacingLeft: 0,
        spacingRight: 0,
        spacingBottom: 0,
        zoomType: canDrag ? ('x' as const) : undefined,
        resetZoomButton: {
          theme: {
            display: 'none',
          },
        },
      },
      xAxis: {
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            // Do not render axis labels when data is loading
            // This prevents the labels from the previous data set floating around when changing to a new data selection
            if (that.args.isLoading) {
              return '';
            }

            const value = this.value as number;

            const isIncompletePeriod =
              (that.isFirstPeriodIncomplete && this.isFirst) ||
              (that.isLastPeriodIncomplete && this.isLast);

            const warningIcon =
              document.getElementById('warning-icon')?.innerHTML;
            const warningSign = isIncompletePeriod ? warningIcon : '';
            const valueFormatted = Highcharts.dateFormat(
              that.dates.highchartsLabelFormats[that.args?.aggregationPeriod],
              value,
            );
            const valueFormattedAndPrefixed =
              that.args?.aggregationPeriod === 'week'
                ? that.intl
                    .t('general.week_of', {
                      date: valueFormatted,
                    })
                    .toString()
                : valueFormatted;

            return [
              `<div class="x-axis-label-with-icon">`,
              `  <div>${valueFormattedAndPrefixed}</div>`,
              `  ${warningSign}`,
              `</div>`,
            ].join('\n');
          },
          useHTML: true,
        },
        type: 'datetime' as const,
        minTickInterval: CHART_INTERVALS[this.args.aggregationPeriod],
        // Needs to be set to half a day, otherwise the afterExtremes event won't trigger
        minRange: CHART_INTERVALS[HALF_DAY],
        events: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          afterSetExtremes(e: Highcharts.AxisSetExtremesEventObject) {
            // We need to use afterSetExtremes instead of set extremes
            // setExtremes executes the moment you let go of the zoom
            // afterSetExtremes only runs after the chart has redrawn
            // This behaviour is better, since we can get unexpected errors if we change chart options during a redraw
            // This does mean we need to check the trigger of the event, since this is not only after a zoom
            if (e.trigger && e.trigger === 'zoom') {
              // Round selection to full periods
              // Add 12 hours to prevent wrong conversion from x value (continuous var) to date
              const zoomStartDate = dayjs.utc(e.min).add(12, 'hour');
              const zoomEndDate = dayjs.utc(e.max).add(12, 'hour');
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              that.updateFilterTask.perform(zoomStartDate, zoomEndDate);
            }
          },
        },
        plotBands:
          that.args?.aggregationPeriod === 'day'
            ? this.chartPlotBands
            : undefined,
      },
      yAxis: this.yAxis,
      tooltip: {
        shared: false,
        crosshairs: true,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,
        positioner: undefined,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter(this: Highcharts.TooltipFormatterContextObject) {
          const dateValue = dayjs.utc(this.point.x, 'x').format('YYYY-MM-DD');
          const stayDate =
            that.filteredAndSortedData.find(
              (item) => item.segmentationValue === dateValue,
            ) ?? null;
          begin();
          set(that, 'tooltipData', stayDate);
          end();
          return document.getElementById('dummy-div-for-staydate-tooltip')
            ?.innerHTML;
        },
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          stacking:
            !this.isCancellationsOnlyView &&
            (this.riSelections?.isTargetActive || this.isCancellationsVisible)
              ? ('normal' as const)
              : undefined,
          // Dynamic column with dependent on the number of data points
          pointWidth: undefined,
          turboThreshold: 0,
          maxPointWidth: 40,
          minPointLength: 3, // Plot a bar for zero values so the tooltip can be inspected
        },
        series: {
          cursor: canClick ? 'pointer' : 'default',
          events: {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            click(e: Highcharts.SeriesClickEventObject) {
              if (canClick) {
                that.usageAnalytics.track('Detailed View Opened', {
                  eventTrigger: 'stay-date-chart-clicked',
                  viewName: 'stay-date',
                });

                const dateClicked = dayjs.utc(e.point.x).format('YYYY-MM-DD');
                that.args.openDayDetail(
                  DETAIL_TABS.RATE_EVOLUTION,
                  dateClicked,
                );
              }
            },
          },
        },
      },
    };
  }

  @computed('filteredAndSortedData.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartPlotBands() {
    if (this.filteredAndSortedData[0]) {
      const dataStart = this.hotelAndMarketService.toLocalTime(
        this.filteredAndSortedData[0]?.segmentationValue,
      );
      const dataEnd = this.hotelAndMarketService.toLocalTime(
        this.filteredAndSortedData[this.filteredAndSortedData.length - 1]
          ?.segmentationValue,
      );
      const days = getPeriodsInRange(dataStart, dataEnd, 'day');

      const returnArray: { color: string; from: number; to: number }[] = [];
      days.forEach((day) => {
        // Do something with `moment`
        if (day.day() === 5 || day.day() === 6) {
          // mark weekends (friday and saturday)
          returnArray.push({
            from: this.hotelAndMarketService.toLocalUnixTime(
              day.subtract(12, 'hour').unix(),
            ),
            to: this.hotelAndMarketService.toLocalUnixTime(
              day.add(12, 'hour').unix(),
            ),
            color: 'var(--prism-color-elevation-canvas)',
          });
        }
      });
      return returnArray;
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentPeriodText() {
    return this.riSelections?.currentPeriodText;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.aggregationPeriod',
    'args.data.[]',
    'riSelections.valuesToCalculate',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredAndSortedData() {
    const rawChartData = this.args.data ?? [];
    const filteredChartData = rawChartData.filter(
      (item) =>
        item.id !==
          `${RI_BREAKDOWNS.IMPACT_DATE}-revenue_insight.unaccounted_transient` &&
        item.id !==
          `${RI_BREAKDOWNS.IMPACT_DATE}-revenue_insight.unaccounted_group`,
    );
    let filteredAndSortedChartData:
      | AggregatedChartData[]
      | RevenueInsightSegmentationValue[] = filteredChartData.sort((a, b) => {
      return (
        new Date(a.segmentationValue).getTime() -
        new Date(b.segmentationValue).getTime()
      );
    });

    if (
      this.args.aggregationPeriod === 'week' ||
      this.args.aggregationPeriod === 'month'
    ) {
      // First we group the chart data in period buckets
      // e.g. when grouped per month:
      // {
      //    '2019-08-01': [SEGMENTATION_OBJECT, SEGMENTATION_OBJECT, ...],
      //    '2019-09-01': [SEGMENTATION_OBJECT, SEGMENTATION_OBJECT, ...],
      //    ...
      // }
      // Use the isoWeek so all week aggregations start on Monday, regardless of the locale
      const aggregationPeriodNormalized =
        NORMALIZED_PERIOD[this.args.aggregationPeriod];
      const chartDataGroupByPeriod = filteredAndSortedChartData
        .filter((item) => !item.isDummy)
        .reduce(
          (groupedData, currentItem) => {
            const currentPeriod = dayjs
              .utc(currentItem.segmentationValue)
              .startOf(aggregationPeriodNormalized)
              .format('YYYY-MM-DD');
            if (!groupedData[currentPeriod]) {
              groupedData[currentPeriod] = [currentItem];
            } else {
              groupedData[currentPeriod].push(currentItem);
            }
            return groupedData;
          },
          {} as Record<
            string,
            [
              RevenueInsightSegmentationValue,
              ...RevenueInsightSegmentationValue[],
            ]
          >,
        );

      // Next we reduce all bucket items to a single object per period
      // e.g:
      // [SEGMENTATION_OBJECT_1, SEGMENTATION_OBJECT_2, ...]
      // Where object_1 would contain the aggregated values of August, and object_2 the values for September
      const chartDataAggregatedByPeriod = Object.keys(
        chartDataGroupByPeriod,
      ).reduce((aggregatedChartData: AggregatedChartData[], currentItem) => {
        const currentPeriodBucket = chartDataGroupByPeriod[currentItem];
        if (!currentPeriodBucket) {
          return aggregatedChartData;
        }

        const firstPeriodBucketItem = currentPeriodBucket[0];

        // Check for incomplete periods
        const firstBucketItemDate = dayjs.utc(
          currentPeriodBucket[0].segmentationValue,
        );
        const lastBucketItemDate = dayjs.utc(
          currentPeriodBucket[currentPeriodBucket.length - 1]
            ?.segmentationValue,
        );
        const beginOfPeriod = firstBucketItemDate.startOf(
          aggregationPeriodNormalized,
        );
        const endOfPeriod = firstBucketItemDate.endOf(
          aggregationPeriodNormalized,
        );
        const isCompletePeriod =
          beginOfPeriod.isSame(firstBucketItemDate, 'day') &&
          endOfPeriod.isSame(lastBucketItemDate, 'day');

        // Start the new period bucket item from a dummy segmentation object and fill in the defaults
        const baseSegmentationObject: Partial<AggregatedChartData> = {
          // @ts-expect-error: Type 'string | null' is not assignable to type 'string | undefined'.
          id: firstPeriodBucketItem.id,
          unfilteredId: firstPeriodBucketItem.unfilteredId,
          segmentationId: firstPeriodBucketItem.segmentationId,
          // For segmentation value, use the first day of the current period
          segmentationValueId: currentItem,
          segmentationValue: currentItem,
          segmentationSortValue: currentItem,
          isIncompletePeriod: !isCompletePeriod,
          totalCapacity: 0,
        };

        // Loop over all the items in the current bucket and sum additive properties to reduce it to a single item
        const currentPeriodBucketHasForecast = currentPeriodBucket.some(
          (item) => item.forecastReference !== null,
        );

        // Keys for which to calculate aggregated values
        // referenceValue depends on if the full period has a complete reference
        const valueTypeKeys = this.riSelections.valuesToCalculate.filter(
          // Only include forecastReference if the period has a forecastReference
          (key) =>
            key !== 'forecastReference' || currentPeriodBucketHasForecast,
        );

        const currentAggregatedPeriod = currentPeriodBucket.reduce(
          (aggregatedPeriod, currentPeriodItem) => {
            valueTypeKeys.forEach((valueTypeKey) => {
              if (!aggregatedPeriod[valueTypeKey]) {
                aggregatedPeriod[valueTypeKey] = {
                  ...EMPTY_SEGMENTATION_VALUE,
                };
              }

              const aggregatedValuetype = aggregatedPeriod[valueTypeKey];

              let currentValueType = null;
              // Special case: if we're in a period that starts in the past and ends in the future,
              // forecast references are only partially available. To prevent wrong pickup last year calculations,
              // we use the actual value for dates which have no forecast reference.
              if (
                valueTypeKey === 'forecastReference' &&
                currentPeriodBucketHasForecast &&
                currentPeriodItem.forecastReference === null
              ) {
                currentValueType = currentPeriodItem.value;
              } else {
                currentValueType = currentPeriodItem[valueTypeKey];
              }

              SUMMABLE_SHORT_KEYS.filter((key) =>
                isSegmentationDataKey(key),
              ).forEach((summableMetricKey) => {
                const currentValue =
                  currentValueType && currentValueType[summableMetricKey];
                const aggregatedValue =
                  aggregatedValuetype && aggregatedValuetype[summableMetricKey];

                if (
                  currentValue === null ||
                  currentValue === undefined ||
                  aggregatedValue === null ||
                  aggregatedValue === undefined ||
                  !aggregatedPeriod[valueTypeKey]
                ) {
                  return;
                }

                aggregatedPeriod[valueTypeKey][summableMetricKey] =
                  aggregatedValue + currentValue;
              });
            });

            // Also sum capacity (separately, since it's not stored in a segmentation item) - totalCapacity only exists for multi property view
            if (aggregatedPeriod.totalCapacity) {
              aggregatedPeriod.totalCapacity +=
                currentPeriodItem.totalCapacity ?? 0;
            }

            return aggregatedPeriod;
          },
          baseSegmentationObject,
        );

        // ADR is not summable so calculate it based on the aggregated bucket item
        valueTypeKeys.forEach((valueTypeKey) => {
          if (currentAggregatedPeriod[valueTypeKey]) {
            currentAggregatedPeriod[valueTypeKey][KPIS.ADR] = safeDivide(
              currentAggregatedPeriod[valueTypeKey][KPIS.REVENUE],
              currentAggregatedPeriod[valueTypeKey][KPIS.NR_OF_ROOMS],
            );
          }
        });

        // Calculate all calculated values
        set(
          currentAggregatedPeriod,
          'calculatedValues',
          CalculatedValues.calculateValues(currentAggregatedPeriod),
        );

        // Add the new aggregated bucket item to an array
        aggregatedChartData.push(
          currentAggregatedPeriod as AggregatedChartData,
        );

        return aggregatedChartData;
      }, []);

      // Finally, set the aggregated period segmentation objects as the chart data
      if (chartDataAggregatedByPeriod.length) {
        filteredAndSortedChartData = chartDataAggregatedByPeriod;
      }
    }
    return filteredAndSortedChartData;
  }

  get inventoryText(): string | undefined {
    if (!this.isCapacityEnabled) {
      return;
    }

    const outOfOrderRooms = new OutOfOrderRooms(getOwner(this)!);

    return outOfOrderRooms.inventoryText;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBudgetEnabled() {
    return this.riSelections?.isBudgetActive;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCancellationsOnlyView() {
    return this.riSelections?.isCancellationsOnlyView;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCapacityEnabled() {
    return this.riSelections?.isCapacityEnabled;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.args.kpiCategory',
    'args.reference',
    'riSelections.isTargetActive',
  )
  get isChangeEnabled(): boolean {
    return !this.riSelections?.isTargetActive;
  }

  @computed('filteredAndSortedData.@each.isIncompletePeriod')
  get isFirstPeriodIncomplete(): boolean {
    const filteredAndSortedDataWithoutDummies =
      this.filteredAndSortedData &&
      this.filteredAndSortedData.filter(
        (item) => !('isDummy' in item && item.isDummy),
      );

    const firstItem = filteredAndSortedDataWithoutDummies.at(0);

    // @ts-expect-error: Property 'isIncompletePeriod' does not exist on type 'RevenueInsightSegmentationValue'.
    return firstItem?.isIncompletePeriod === true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isForecastEnabled() {
    return this.riSelections?.isForecastActive;
  }

  /**
   * Checks if kpiCategory group was selected
   * @return {Boolean} True if the kpiCategory equals KPI_CATEGORIES.GROUP ('group')
   */
  get isGroupsSelected(): boolean {
    return this.args.kpiCategory === KPI_CATEGORIES.GROUP;
  }

  @computed('filteredAndSortedData.@each.isIncompletePeriod')
  get isLastPeriodIncomplete(): boolean {
    const filteredAndSortedDataWithoutDummies =
      this.filteredAndSortedData &&
      this.filteredAndSortedData.filter(
        (item) => !('isDummy' in item && item.isDummy),
      );

    const lastItem = filteredAndSortedDataWithoutDummies.at(-1);

    // @ts-expect-error: Property 'isIncompletePeriod' does not exist on type 'RevenueInsightSegmentationValue'.
    return lastItem?.isIncompletePeriod === true;
  }

  // Still uses riSelections.isToDateInFuture since looking at the points is no option here, best effort check to enable it is done based on selected dates
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPickupLastYearEnabled() {
    return (
      this.riSelections?.isTargetActive &&
      this.args.kpi !== KPIS.ADR &&
      this.riSelections?.isToDateInFuture &&
      this.riSelections.showPickupLY
    );
  }

  /**
   * Shows rates data legend item as enabled/disabled
   * @return {Boolean} True if rates serie should be shown as enabled
   */
  get isRatesDataActive(): boolean {
    return (
      this.isRatesVisible &&
      !this.ratesDisabledForSelection &&
      !this.ratesDataEmpty
    );
  }

  /**
   * Creates zones and series data with the rates info
   * Returns an object which contains zones as well as a data array containing points
   * @return {Object} Object containing the zones and the data for the rates serie
   */
  @computed(
    'filteredAndSortedData.@each.compsetDifference',
    'hotelAndMarketService.hotel.hotelinfo.id',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesData() {
    const zones: Highcharts.SeriesZonesOptionsObject[] = [];
    const data: RatesPoint[] = [];
    let previousPoint: RatesPoint | null = null;
    let hasNoData = true; // Used for detecting if there are rates, but they are all soldout or similar
    this.filteredAndSortedData.some((item, index) => {
      if ('isDummy' in item && item.isDummy) {
        // If item is a dummy segmentation, ignore and continue to next item
        return false;
      }
      if (!('rates' in item && item.rates)) {
        // If item does not contain rates (not empty, but actually not on the object) then there was no rates data, we hide the entire chart
        hasNoData = false; // Saying that it's no data in this case would be incorrect. The rates are not there because no request was made (not allowed for selection)
        return true;
      }

      // Current point which will be returned in this map
      let currentPoint: RatesPoint;
      // Own rate value
      const ourHotelinfoId = this.hotelAndMarketService.hotel?.hotelinfo?.id;

      if (!ourHotelinfoId || !item.rates[ourHotelinfoId]) {
        return true;
      }

      const ownRate = item.rates[ourHotelinfoId][0]?.value;

      if (!ownRate || ownRate === 0) {
        // If there's no data, make a no-data zone, and set cross-marker

        // Create a point for tomorrow, used to make a no-data-zone
        const nextPoint = {
          x: xValueTomorrow(item.segmentationValue) * 1000,
        };

        if (previousPoint) {
          addNoDataZone(zones, previousPoint, nextPoint, index);
        }

        currentPoint = {
          y: previousPoint ? previousPoint.y : undefined,
          x: this.hotelAndMarketService.toLocalUnixTime(
            this.hotelAndMarketService
              .toLocalTime(item.segmentationValue)
              .unix(),
          ),
          segmentationValueObj: item,
          marker: HIGHCHARTS_MISSING_MARKER,
        };
      } else {
        // If data is present, add normal zones if needed
        const lastZone = zones[zones.length - 1];
        if (lastZone && lastZone.dashStyle === NO_DATA_ZONE.dashStyle) {
          addZone(zones, xValueTomorrow(item.segmentationValue) * 1000);
        }

        // Make an array of rates like: [145, 514, 89, ...] for the median util
        const ratesAsValuesArray = Object.keys(item.rates)
          .filter(
            (hotelinfoId) =>
              hotelinfoId !== this.hotelAndMarketService.hotel?.hotelinfo?.id,
          )
          .map((hotelinfoId) => item.rates?.[hotelinfoId]?.[0]?.value)
          .filter((value): value is number => value !== undefined && value > 0);

        const median = findMedianRate(ratesAsValuesArray);

        currentPoint = {
          y:
            typeof median === 'number'
              ? compsetDifference(ownRate, median) * 100
              : null,
          x: this.hotelAndMarketService.toLocalUnixTime(
            this.hotelAndMarketService
              .toLocalTime(item.segmentationValue)
              .unix(),
          ),
          segmentationValueObj: item,
        };

        // As soon as there is data on any point, turn off no data flag
        // This is used as a way to indicate that rates were loaded, but they had no values (soldout, LOS3, etc...)
        hasNoData = false;
      }

      // Remember this as the previous point for the next iteration
      previousPoint = currentPoint;

      data.push(currentPoint);

      // Continue looping over rates
      return false;
    });

    return {
      data,
      zones,
      hasNoData,
    };
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesDataEmpty() {
    return this.ratesData?.hasNoData;
  }

  /**
   * Specifically checks if rates are disabled because the selection does not allow it
   * Extra check on groups because this is not checked for in the request!
   * @return {Boolean} True if the rates serie cannot be enabled because of the selection
   */
  @dependentKeyCompat
  get ratesDisabledForSelection(): boolean {
    return (
      !this.ratesData?.data?.length ||
      this.isGroupsSelected ||
      this.args.aggregationPeriod === 'week' ||
      this.args.aggregationPeriod === 'month'
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodNegativeChangeText() {
    return this.riSelections?.referencePeriodNegativeChangeText;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriodPositiveChangeText() {
    return this.riSelections?.referencePeriodPositiveChangeText;
  }

  /**
   * First period in the chart
   * @return { int }   : unix timestamp for the start of the requested impact_dates
   */
  get xAxisFloor(): number {
    let baseFromDate = this.args.fromDate;
    // The stayDate filter has priority over the regular fromDate query param
    if (this.args.parsedStayDateFilter) {
      baseFromDate = this.args.parsedStayDateFilter?.startDate;
    }

    if (this.args.aggregationPeriod === 'day') {
      return dayjs.utc(baseFromDate).startOf('day').valueOf();
    }

    // Use the start of the period when aggregation as x min
    const aggregationPeriodNormalized =
      NORMALIZED_PERIOD[this.args.aggregationPeriod];
    return dayjs
      .utc(baseFromDate)
      .startOf(aggregationPeriodNormalized)
      .valueOf();
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxis() {
    const title = this.intl.t(`revenue_insight.${this.riSelections.kpi}`);

    const { isOccupancySelected, isCancellationsPercentageView } =
      this.riSelections;

    const primaryAxis: Highcharts.YAxisOptions = {
      title: {
        text: title.toString(),
        style: {
          color: 'var(--prism-color-text-neutral-subdued)',
          fill: 'var(--prism-color-text-neutral-subdued)',
        },
      },
      max: this.yAxisLimits?.max,
      min: -this.yAxisLimits?.max,
      tickAmount: 5,
      labels: {
        // Explicitely set the formatter to null by default so when toggling off the percentage format it gets replaced by this
        // doing this fixes https://otainsight.atlassian.net/browse/SH-12269
        formatter: undefined,
      },
    };

    if (isOccupancySelected || isCancellationsPercentageView) {
      primaryAxis.labels = {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          return `${this.value}%`;
        },
      };
    }

    if (this.args.isRatesEnabled && !this.ratesDisabledForSelection) {
      const secondaryAxis: Highcharts.YAxisOptions = {
        title: {
          text: this.intl
            .t('general.compset_difference_own_hotel.short')
            .toString(),
          style: {
            color: 'var(--prism-color-text-neutral-subdued)',
            fill: 'var(--prism-color-text-neutral-subdued)',
          },
        },
        labels: {
          formatter() {
            return `${this.value}%`;
          },
        },
        max: this.yAxisLimits?.ratesMax,
        min: -this.yAxisLimits?.ratesMax,
        tickAmount: 5,
        opposite: true,
      };

      return [primaryAxis, secondaryAxis];
    }
    return [primaryAxis];
  }

  /**
   * determines the limits for all active y axes
   * Will ensure these are the same ratio, this way the 0s of both axes align
   */
  @computed('chartData.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxisLimits() {
    let max = 0;
    let ratesMax = 0;

    this.chartData.forEach((series) => {
      const isRates = series.yAxis === 1;
      if (series.visible || isRates) {
        series.data.forEach((dataPoint) => {
          if (!isRates) {
            // If the series are stacked, pick the stacked value instead to determine the max
            const currentValue =
              'stackedY' in dataPoint && dataPoint.stackedY
                ? dataPoint.stackedY
                : dataPoint.y;
            const absoluteCurrentValue = currentValue
              ? Math.abs(currentValue)
              : currentValue;
            if (absoluteCurrentValue && absoluteCurrentValue > max) {
              max = absoluteCurrentValue;
            }
          } else {
            const absoluteCurrentValue = dataPoint.y
              ? Math.abs(dataPoint.y)
              : dataPoint.y;
            if (absoluteCurrentValue && absoluteCurrentValue > ratesMax) {
              ratesMax = absoluteCurrentValue;
            }
          }
        });
      }
    });

    return {
      max,
      ratesMax,
    };
  }

  /**
   * Toggles the rates data only if this is allowed
   */
  @action
  toggleRatesData(): void {
    // Prevent toggling rates data when kpiCategory is 'group'.
    if (!this.ratesDisabledForSelection && !this.ratesDataEmpty) {
      set(this, 'isRatesVisible', !this.isRatesVisible);
    }
  }

  /**
   * Toggles the visiblity of series
   * @param  { string } property : the property that handles the visibility of the series
   */
  @action
  toggleSeries(
    property:
      | 'isCancellationsThisYearVisible'
      | 'isCancellationsLastYearVisible'
      | 'isThisYearVisible'
      | 'isCancellationsVisible'
      | 'isChangeVisible'
      | 'isPickupLastYearVisible'
      | 'isBudgetVisible'
      | 'isForecastVisible'
      | 'isCapacityVisible',
  ): void {
    const isVisible = get(this, property);
    set(this, property, !isVisible);

    if (property === 'isCancellationsVisible') {
      set(this, 'isChangeVisible', !this.isCancellationsVisible);
      set(this, 'isCapacityVisible', !this.isCancellationsVisible);
      set(this, 'isRatesVisible', !this.isCancellationsVisible);
    }
  }

  @action
  updateCapacityVisibility(): void {
    // Capacity is only visible by default if you don't have budget, forecast nor filters active. Also don't showit for cancellations only by default
    set(
      this,
      'isCapacityVisible',
      !this.riSelections?.isTargetActive &&
        !this.args.filtersActive &&
        !this.riSelections.isCancellationsOnlyView,
    );
  }

  <template>
    <div
      {{didInsert this.updateCapacityVisibility}}
      {{didUpdate
        this.updateCapacityVisibility
        @filtersActive
        this.riSelections.isTargetActive
        this.riSelections.isCancellationsOnlyView
      }}
    >
      <GeneralChartsOtaChart
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
        @onUpdateChartReference={{@onUpdateChartReference}}
      />
      <div class="stay-dates chart-legend mt-400">
        {{#if this.riSelections.isCancellationsOnlyView}}
          <GeneralChartsLegendsLegendItem
            @id="cancellations--this-year"
            @isHidden={{not this.isCancellationsThisYearVisible}}
            @legendItemClicked={{fn
              this.toggleSeries
              "isCancellationsThisYearVisible"
            }}
            @type="line"
          >
            {{this.riSelections.currentCancellationsPeriodText}}
          </GeneralChartsLegendsLegendItem>
          <GeneralChartsLegendsLegendItem
            @id="cancellations--last-year"
            @isHidden={{not this.isCancellationsLastYearVisible}}
            @legendItemClicked={{fn
              this.toggleSeries
              "isCancellationsLastYearVisible"
            }}
            @type="line"
          >
            {{this.riSelections.referenceCancellationsPeriodText}}
          </GeneralChartsLegendsLegendItem>
        {{else}}
          <GeneralChartsLegendsLegendItem
            @id="normal"
            @isHidden={{not this.isThisYearVisible}}
            @legendItemClicked={{fn this.toggleSeries "isThisYearVisible"}}
            @type="dot"
          >
            {{this.currentPeriodText}}
          </GeneralChartsLegendsLegendItem>
          {{#if this.riSelections.hasCancellations}}
            <GeneralChartsLegendsLegendItem
              @id="cancellations"
              @isHidden={{not this.isCancellationsVisible}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isCancellationsVisible"
              }}
              @type="line"
            >
              {{t "revenue_insight.legend.cancellations"}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
          {{#if this.isChangeEnabled}}
            <GeneralChartsLegendsLegendItem
              @id="positive-change"
              @isHidden={{not this.isChangeVisible}}
              @legendItemClicked={{fn this.toggleSeries "isChangeVisible"}}
              @type="dot"
            >
              {{this.referencePeriodPositiveChangeText}}
            </GeneralChartsLegendsLegendItem>
            <GeneralChartsLegendsLegendItem
              @id="negative-change"
              @isHidden={{not this.isChangeVisible}}
              @legendItemClicked={{fn this.toggleSeries "isChangeVisible"}}
              @type="dot"
            >
              {{this.referencePeriodNegativeChangeText}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
          {{#if
            (and
              this.isPickupLastYearEnabled (not this.isCancellationsOnlyView)
            )
          }}
            <GeneralChartsLegendsLegendItem
              @id="pickup-ly"
              @isHidden={{not this.isPickupLastYearVisible}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isPickupLastYearVisible"
              }}
              @type="dot"
            >
              {{this.riSelections.leftToPickupText}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
          {{#if (and this.isBudgetEnabled (not this.isCancellationsOnlyView))}}
            <GeneralChartsLegendsLegendItem
              @id="budget"
              @isHidden={{not this.isBudgetVisible}}
              @legendItemClicked={{fn this.toggleSeries "isBudgetVisible"}}
              @type="line"
            >
              {{t "revenue_insight.budget"}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
          {{#if
            (and this.isForecastEnabled (not this.isCancellationsOnlyView))
          }}
            <GeneralChartsLegendsLegendItem
              @id="forecast"
              @isHidden={{not this.isForecastVisible}}
              @legendItemClicked={{fn this.toggleSeries "isForecastVisible"}}
              @type="line"
            >
              {{t "general.forecast"}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
        {{/if}}
        {{#if @isRatesEnabled}}
          <GeneralChartsLegendsLegendItem
            @id="rates"
            @isHidden={{not this.isRatesDataActive}}
            @legendItemClicked={{this.toggleRatesData}}
            @type="line"
          >
            {{t "general.compset_difference_own_hotel"}}
            {{#if this.ratesDataEmpty}}
              <PrismTooltip @text={{t "revenue_insight.no_rates_available"}} />
            {{else if this.ratesDisabledForSelection}}
              <PrismTooltip
                @text={{t "revenue_insight.not_available_for_selection"}}
              />
            {{/if}}
          </GeneralChartsLegendsLegendItem>
        {{/if}}
        {{#if this.isCapacityEnabled}}
          <GeneralChartsLegendsLegendItem
            @id="capacity"
            @isHidden={{not this.isCapacityVisible}}
            @legendItemClicked={{fn this.toggleSeries "isCapacityVisible"}}
            @type="line"
          >
            {{this.inventoryText}}
          </GeneralChartsLegendsLegendItem>
        {{/if}}
      </div>
      <div class="hidden" id="dummy-div-for-staydate-tooltip">
        <div class="revenue-insight-segmentation-value-tooltip">
          {{! added if here since this tooltip has some instensive calculations and I wanted to avoid doing them if there was no data}}
          {{#if this.tooltipData}}
            <HotelAndMarketRevenueInsightTooltipsTooltipStayDate
              @aggregationPeriod={{@aggregationPeriod}}
              @data={{this.tooltipData}}
              @isGroupsSelected={{this.isGroupsSelected}}
              {{! @glint-expect-error: Incorrect type }}
              @kpiCategory={{@kpiCategory}}
              @kpis={{@visibleKpis}}
              @metricKey={{@kpi}}
            />
          {{/if}}
        </div>
      </div>
    </div>
    <div class="hidden" id="warning-icon">
      <PrismIcon @color="warning" @name="warning" @size="extra-small" />
    </div>
  </template>
}
